import React from 'react';
import { useIntl } from 'react-intl';
import { Container, Row, Col, Button, Alert, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart as faHeartRegular } from '@fortawesome/pro-light-svg-icons/faHeart';
import { TypedMemo } from '../common/types';
import { SMLContainer } from '../smartLinks/smlContainer/SMLContainer';
import { SMLBreadcrumbs } from '../smartLinks/smlBreadcrumbs/SMLBreadcrumbs';
import { SMLRouteNames } from '../../routing/sml/SMLRoutes';
import { usePlaylistDetails } from './hooks/usePlaylistDetails';
import { usePlaylistFollow } from './hooks/usePlaylistFollow';
import { useSMLDatePickerDateParam } from './hooks/useSMLDatePickerDateParam';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { LoadingView } from '../common/views/LoadingView';
import { SMLDatePicker } from './components/SMLDatePicker/SMLDatePicker';
import { IntegrationTypeIcon } from './components/IntegrationTypeIcon';
import { PlaylistImageMosaic } from './components/PlaylistImageMosaic/PlaylistImageMosaic';
import { PlaylistSongsList } from './components/PlaylistSongsList/PlaylistSongsList';
import { PlaylistChangeTimeline } from './components/PlaylistChangeTimeline/PlaylistChangeTimeline';
import { PlaylistFollowersChart } from './components/PlaylistFollowersChart/PlaylistFollowersChart';
import { SharePubliclyButton } from './components/SharePublicly/SharePubliclyButton';
import { PublicModeError } from './components/PublicModeError/PublicModeError';
import { useShareTokenContext } from './contexts/ShareTokenContext';
import { useEntityIdParam } from './hooks/useEntityIdParam';
import { ChartsBackLink } from './charts/ChartsBackLink';
import { useChartsDetailCrumbs } from './charts/useChartsDetailCrumbs';
import { useMobilePageTitle } from './navigation/hooks/useMobilePageTitle';

const formatFollowersCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(0)}K`;
  }
  return count.toString();
};

const formatLastUpdated = (dateString: string, intl: ReturnType<typeof useIntl>): string => {
  const dateValue = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - dateValue.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) {
    return intl.formatMessage({ id: 'app.sml.playlists.updated.justNow', defaultMessage: 'Just now' });
  }
  if (diffHours < 24) {
    return intl.formatMessage(
      { id: 'app.sml.playlists.updated.hoursAgo', defaultMessage: '{hours}h ago' },
      { hours: diffHours }
    );
  }
  return intl.formatMessage(
    { id: 'app.sml.playlists.updated.daysAgo', defaultMessage: '{days}d ago' },
    { days: diffDays }
  );
};

const SMLPlaylistDetailsComponent: React.FC = () => {
  const intl = useIntl();
  const { payload, isPublicMode } = useShareTokenContext();
  const playlistId = useEntityIdParam('playlist');

  const { selectedDate, rangeDays } = useSMLDatePickerDateParam();

  const {
    data: playlistData,
    isLoading,
    error,
    refetch,
  } = usePlaylistDetails({
    playlistId: playlistId ?? '',
  });
  // Design b4: the mobile top bar names the entity of this deep page.
  useMobilePageTitle(playlistData?.name);

  const {
    isFollowing,
    isLoading: isFollowLoading,
    toggleFollow,
  } = usePlaylistFollow({
    playlistId: playlistId ?? '',
  });

  const crumbName =
    playlistData?.name ??
    intl.formatMessage({ id: 'app.sml.breadcrumbs.playlist-details', defaultMessage: 'Playlist Details' });
  const breadcrumbs = useChartsDetailCrumbs(SMLRouteNames.playlists, crumbName, isPublicMode);
  // design navigation-v2 e 5c: a page under Charts has a back link; a public share link has none.
  const back = isPublicMode ? null : <ChartsBackLink tab={SMLRouteNames.playlists} />;

  if (isPublicMode && payload?.t !== 'playlist') {
    return (
      <SMLContainer publicMode header={null}>
        <PublicModeError />
      </SMLContainer>
    );
  }

  if (playlistId === null) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <SMLDatePicker showRangePresets />
          </SMLBreadcrumbs>
        }
      >
        {back}
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>
            {intl.formatMessage({ id: 'app.sml.playlist.details.no-id', defaultMessage: 'No playlist ID provided' })}
          </p>
        </div>
      </SMLContainer>
    );
  }

  if (error !== null && error !== undefined && isPublicMode) {
    return (
      <SMLContainer publicMode header={null}>
        <PublicModeError />
      </SMLContainer>
    );
  }

  if (error !== null && error !== undefined) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <SMLDatePicker showRangePresets />
          </SMLBreadcrumbs>
        }
      >
        {back}
        <ErrorView
          error={error}
          retry={() => {
            refetch().catch(console.error);
          }}
        />
      </SMLContainer>
    );
  }

  return (
    <SMLContainer
      publicMode={isPublicMode}
      header={
        <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
          <SMLDatePicker showRangePresets />
        </SMLBreadcrumbs>
      }
    >
      {back}
      <Container fluid>
        {/* Playlist Header - shows inline loading while metadata loads */}
        <Row className="mb-4">
          <Col>
            {isLoading ? (
              <LoadingView />
            ) : (
              <div className="d-flex align-items-center gap-3">
                <PlaylistImageMosaic
                  trackImageUrls={playlistData?.trackImageUrls ?? []}
                  fallbackImageUrl={playlistData?.imageUrl ?? ''}
                  name={playlistData?.name ?? 'Playlist'}
                  size={80}
                />
                <div className="flex-grow-1">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <h1 className="mb-1">{playlistData?.name}</h1>
                    <div className="d-flex align-items-center gap-2">
                      {!isPublicMode && (
                        <SharePubliclyButton
                          type="playlist"
                          id={playlistId}
                          shareTitle={intl.formatMessage(
                            {
                              id: 'app.sml.sharePublicly.title.playlist',
                              defaultMessage: '{name} on MUSIC24',
                            },
                            { name: playlistData?.name ?? '' }
                          )}
                          name={playlistData?.name}
                          coverUrl={playlistData?.imageUrl ?? undefined}
                          subtitle={intl.formatMessage({
                            id: 'app.sml.sharePublicly.type.playlist',
                            defaultMessage: 'Playlist',
                          })}
                        />
                      )}
                      <Button
                        color={isFollowing ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={toggleFollow}
                        disabled={isFollowLoading || isPublicMode}
                        className="d-flex align-items-center"
                      >
                        <FontAwesomeIcon icon={isFollowing ? faHeartSolid : faHeartRegular} />
                        {isFollowing
                          ? intl.formatMessage({ id: 'app.sml.playlists.following', defaultMessage: 'Following' })
                          : intl.formatMessage({ id: 'app.sml.playlists.follow', defaultMessage: 'Follow' })}
                      </Button>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {playlistData?.curatorName !== undefined && playlistData?.curatorName !== null && (
                      <span className="text-muted">
                        {intl.formatMessage(
                          { id: 'app.sml.playlists.by', defaultMessage: 'by {curator}' },
                          { curator: playlistData.curatorName }
                        )}
                      </span>
                    )}
                    <IntegrationTypeIcon integrationType={playlistData?.integrationType ?? ''} size={20} />
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Stats Row - only show when data is loaded */}
        {!isLoading && playlistData !== undefined && (
          <Row className="mb-4">
            <Col>
              <div className="d-flex gap-5">
                <div>
                  <div className="text-muted small">
                    {intl.formatMessage({ id: 'app.sml.playlists.followers', defaultMessage: 'Followers' })}
                  </div>
                  <div className="h4 mb-0">{formatFollowersCount(playlistData.followersCount ?? 0)}</div>
                </div>
                <div>
                  <div className="text-muted small">
                    {intl.formatMessage({ id: 'app.sml.playlists.songs', defaultMessage: 'Songs' })}
                  </div>
                  <div className="h4 mb-0">{playlistData.songsCount}</div>
                </div>
                <div>
                  <div className="text-muted small">
                    {intl.formatMessage({ id: 'app.sml.playlists.lastUpdated', defaultMessage: 'Last Updated' })}
                  </div>
                  <div className="h4 mb-0">{formatLastUpdated(playlistData.lastUpdated ?? '', intl)}</div>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {/* Pending analysis banner */}
        {playlistData?.hasFetchedData === false && (
          <Row className="mb-4">
            <Col>
              <Alert color="info" className="d-flex align-items-center gap-2 mb-0">
                <Spinner size="sm" />
                {intl.formatMessage({
                  id: 'app.sml.playlists.scheduledForAnalysis',
                  defaultMessage:
                    'This playlist has been scheduled for analysis. Data will appear once processing is complete.',
                })}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Followers Chart */}
        {playlistData?.hasFetchedData !== false && (
          <Row className="mb-4">
            <Col>
              <PlaylistFollowersChart playlistId={playlistId} endDate={selectedDate} rangeDays={rangeDays} />
            </Col>
          </Row>
        )}

        {/* Songs List and Change Timeline - render immediately, they load their own data */}
        {playlistData?.hasFetchedData !== false && (
          <Row>
            <Col md={7} className="mb-4">
              <PlaylistSongsList playlistId={playlistId} lastUpdated={playlistData?.lastUpdated} />
            </Col>
            <Col md={5} className="mb-4">
              <PlaylistChangeTimeline playlistId={playlistId} endDate={selectedDate} />
            </Col>
          </Row>
        )}
      </Container>
    </SMLContainer>
  );
};

export const SMLPlaylistDetails = TypedMemo(SMLPlaylistDetailsComponent);
